import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import AuthLayout from "@/components/layout/AuthLayout";
import { GoogleButton } from "@/components/GoogleButton";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { t } = useT();
  const { setCtx } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/register", form);
      setCtx(data);
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("auth.register.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("auth.register.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="register-error">
            {error}
          </div>
        )}
        {[
          { k: "name", type: "text", label: t("auth.name"), ph: "Ahmed El Fassi", tid: "register-name-input" },
          { k: "email", type: "email", label: t("auth.email"), ph: "vous@entreprise.com", tid: "register-email-input" },
          { k: "password", type: "password", label: t("auth.password"), ph: "8 caractères minimum", tid: "register-password-input" },
        ].map((f) => (
          <div key={f.k}>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{f.label}</label>
            <input
              type={f.type} required value={form[f.k]} onChange={upd(f.k)}
              data-testid={f.tid} placeholder={f.ph} minLength={f.k === "password" ? 8 : undefined}
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        ))}
        <button
          type="submit" disabled={busy} data-testid="register-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("auth.register.cta")}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs uppercase tracking-wider text-slate-400">{t("auth.or")}</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label={t("auth.google")} />

      <p className="mt-8 text-center text-sm text-slate-500">
        {t("auth.have_account")}{" "}
        <Link to="/login" data-testid="go-login-link" className="font-semibold text-primary hover:underline">
          {t("auth.login.title")}
        </Link>
      </p>
    </AuthLayout>
  );
}
